/* assets/gcitypartners-capability-gap.js — GCITYPARTNERS-CAPABILITY-GAP-PROJECTION-01
 * Child projection only. The Supplier Network remains the search/RFQ owner.
 * Computes requested minus rendered provider capabilities for display only.
 * Does not certify eligibility, rank providers, create RFQs or mutate Provider data.
 */
(function (window, document) {
  'use strict';

  var ROOT = '[data-gcp-supplier-network]';
  const mounted = new WeakSet();
  /**
   * @typedef {Readonly<{
   *   mountAll: (scope?: ParentNode | null) => void,
   *   requested: (root: Element) => string[],
   *   normalize: (value: unknown) => string
   * }>} CapabilityGapProjection
   */
  /**
   * @type {Window & {
   *   GCityPartnersCapacityActivation?: {
   *     mountAll?: (scope?: ParentNode | null) => void
   *   },
   *   GCityPartnersCapabilityGapProjection?: CapabilityGapProjection
   * }}
   */
  const projectionWindow = window;
  const existingActivation = projectionWindow.GCityPartnersCapacityActivation;

  if (existingActivation && typeof existingActivation.mountAll === 'function') {
    existingActivation.mountAll(document);
    return;
  }

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }
  function isZh() {
    return /^zh/i.test(clean(document.documentElement.lang));
  }
  function words(zh, en) {
    return isZh() ? zh : en;
  }
  /** @param {unknown} value */
  function normalize(value) {
    return clean(value)
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\u3400-\u9fff]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }
  function requested(root) {
    var input = root.querySelector('[data-gcp-supplier-search-form] [name="capabilities"]');
    var seen = Object.create(null);
    return clean(input && input.value)
      .split(/[\n,]+/)
      .map(normalize)
      .filter(function (id) {
        if (!id || seen[id]) return false;
        seen[id] = true;
        return true;
      })
      .slice(0, 12);
  }
  function providerCapabilities(card) {
    const raw = card.getAttribute('data-gcp-provider-capability-ids');
    if (raw === null) return null;

    try {
      const values = JSON.parse(raw);
      if (
        !Array.isArray(values) ||
        !values.every(function (value) {
          return typeof value === 'string' && value.length > 0;
        })
      )
        return null;

      return values.map(normalize).filter(Boolean);
    } catch (_error) {
      return null;
    }
  }
  /**
   * @param {Element} root
   * @param {Element} card
   */
  function renderCard(root, card) {
    const required = requested(root);
    const existing = providerCapabilities(card);
    const old = card.querySelector('[data-gcp-capability-gap]');
    if (old) old.remove();
    if (!required.length) return;

    const known = existing !== null;
    const missing = known
      ? required.filter(function (id) {
          return existing?.indexOf(id) === -1;
        })
      : [];

    const line = document.createElement('div');
    line.className = 'gcp-supplier-network__gap';
    line.setAttribute('data-gcp-capability-gap', '');
    line.setAttribute('data-gap-state', known ? 'available' : 'unknown');

    if (known) {
      line.setAttribute('data-gap-count', String(missing.length));
      line.setAttribute('data-full-capability-coverage', String(missing.length === 0));
    }

    const strong = document.createElement('strong');
    strong.textContent = !known
      ? words('能力資料待確認', 'Capability data unavailable')
      : missing.length
        ? words('候選合作夥伴能力待完善', 'Supplier capability gaps')
        : words('要求的能力標籤已匹配', 'Requested capability tags matched');
    line.appendChild(strong);

    const value = document.createElement('span');
    value.textContent = !known
      ? words('暫時無法比較，請重新搜尋。', 'Unable to compare. Search again.')
      : missing.length
        ? missing.join(' · ')
        : words(
            '本次返回資料未見能力標籤待完善。',
            'No capability-tag gap found in the returned data.'
          );
    line.appendChild(value);

    const note = document.createElement('small');
    note.textContent = words(
      '比較搜尋要求與此合作夥伴返回的能力標籤；不代表已核實資質、Company A 自身能力或投標資格。',
      'Compares search requirements with this supplier’s returned capability tags, not verified qualifications, Company A’s own capabilities or bid eligibility.'
    );
    line.appendChild(note);

    const actions = card.querySelector('.gcp-supplier-network__actions');
    if (actions) card.insertBefore(line, actions);
    else card.appendChild(line);
  }
  function render(root) {
    Array.prototype.forEach.call(
      root.querySelectorAll('.gcp-supplier-network__supplier'),
      function (card) {
        renderCard(root, card);
      }
    );
  }
  function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);
    var results = root.querySelector('[data-gcp-supplier-results]');
    var form = root.querySelector('[data-gcp-supplier-search-form]');
    if (results && typeof MutationObserver === 'function') {
      new MutationObserver(function () {
        render(root);
      }).observe(results, { childList: true });
    }
    if (form)
      form.addEventListener('submit', function () {
        window.setTimeout(function () {
          render(root);
        }, 0);
      });
    render(root);
  }
  /** @param {ParentNode | null} [scope] */
  function mountAll(scope) {
    (scope || document).querySelectorAll(ROOT).forEach(mount);
  }

  window.GCityPartnersCapabilityGapProjection = Object.freeze({
    mountAll: mountAll,
    requested: requested,
    normalize: normalize,
  });
  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target || document);
  });
  if (document.readyState === 'loading')
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        mountAll(document);
      },
      { once: true }
    );
  else mountAll(document);
})(window, document);
